import React, { useMemo, useState } from 'react';
import VendorInstantPayoutModal from '@components/orders/InstantPayoutModal/VendorInstantPayoutModal';
import { useVendor } from '@context/VendorContext';
import { Caption } from '@goodfynd/typography';
import { Button, Link, Text } from '@goodfynd/ui';
import { useScreenSmall } from '@goodfynd/theme';
import { asCurrency, getAmountDisplay, getCurrency, getWholeNumber, renderPrice } from '@goodfynd/utils';
import { useVendorPayouts } from '@hooks/usePayouts';
import Skeleton from '@mui/material/Skeleton';
import { calculateInstantPayoutFee } from '@utils/order-util';
import { dateUtil } from 'utils/index';
import { getDashboardRoutePath } from 'utils/routes-util';

import config from '../../../config';
import routes from '../../../config/routes';
import { OverviewCard } from '../overview-card';

export default function VendorPayoutOverview() {
  const isSmallScreen = useScreenSmall();

  const [showInstantPayoutModal, toggleInstantPayoutModal] = useState(false);
  const { balances, isLoading, payouts } = useVendorPayouts();

  const { vendorId } = useVendor();

  const { availableBalance, pendingBalance } = useMemo(
    () => ({
      availableBalance: asCurrency(
        balances?.available > 0 ? balances.available : 0
      ),
      pendingBalance: balances?.pending || 0,
    }),
    [balances.available, balances?.pending]
  );

  const lastPayout = useMemo(
    () => payouts.find((payout) => payout.status === 'PAID'),
    [payouts]
  );

  const amountDisplay = useMemo(() => {
    return getAmountDisplay(availableBalance, {
      mantissa: 0,
    });
  }, [availableBalance]);

  const cents = useMemo(() => {
    return getWholeNumber(
      (availableBalance - Math.floor(availableBalance)) * 100
    );
  }, [availableBalance]);

  const maxPayout = useMemo(
    () =>
      Number(
        getCurrency(
          balances.instant - calculateInstantPayoutFee(balances.instant),
          { thousandSeparated: false }
        )
      ),
    [balances.instant]
  );

  return (
    <>
      <OverviewCard id="overview-payout" showDateFilter={false} title="Payout">
        {isLoading ? (
          <>
            <Text as="heading1">
              <Skeleton width="25%" />
            </Text>
            <Text as="caption" color="TextIconPrimary" tag="span">
              <Skeleton width="75%" />
            </Text>

            <Text as="body2" style={{ marginTop: 32 }}>
              <Skeleton width="100%" />
            </Text>
            <Text as="body2">
              <Skeleton width="15%" />
            </Text>

            <Text as="heading1">
              <Skeleton width="45%" />
            </Text>
          </>
        ) : (
          <>
            {isSmallScreen && availableBalance > 0 && (
              <Caption>{renderPrice(availableBalance)}</Caption>
            )}

            <Text as="heading1" title={renderPrice(availableBalance)}>
              <Text
                as="heading3"
                style={{ position: 'relative', top: 3 }}
                tag="sup"
              >
                $
              </Text>

              {amountDisplay}

              {availableBalance < 1000 && cents > 0 && (
                <Text
                  as="heading3"
                  style={{ position: 'relative', top: 3 }}
                  tag="sup"
                >
                  {cents.toString().padStart(2, '0')}
                </Text>
              )}
            </Text>

            <Text as="caption" color="TextIconPrimary" tag="span">
              Available for payout
              {pendingBalance > 0 && (
                <span>&nbsp;with {renderPrice(pendingBalance)} pending</span>
              )}
            </Text>

            {lastPayout && (
              <Text as="body2" style={{ marginTop: 32 }}>
                Last payout was on&nbsp;
                {dateUtil.format(lastPayout.date, config.dateFormats.monthDay)}
                &nbsp;for&nbsp;
                {renderPrice(lastPayout.amount)}.
              </Text>
            )}

            <Link
              display="underline"
              href={getDashboardRoutePath(
                routes.dashboard.reports.payouts,
                vendorId
              )}
            >
              <Text as="body2">View payout history</Text>
            </Link>

            {maxPayout > 0 && (
              <Button
                margin="24px 0 0"
                onClick={() => toggleInstantPayoutModal(true)}
                size="medium"
              >
                {`Get paid ${renderPrice(balances.instant)} instantly`}
              </Button>
            )}
          </>
        )}
      </OverviewCard>

      {showInstantPayoutModal && (
        <VendorInstantPayoutModal
          close={() => toggleInstantPayoutModal(false)}
          open
        />
      )}
    </>
  );
}
